import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import { useCallback, useEffect, useState } from "react";
import agent from "../../app/api/agent";
import { DataSyncAreaStatus, DataSyncStatus } from "../../app/models/DataSync";

const formatDateTime = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const toTimeValue = (value?: string | null) => {
    if (!value) return 0;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getLatestCompletionTime = (status?: DataSyncStatus) => {
    if (!status) return 0;

    return Math.max(
        toTimeValue(status.catalog.lastCompletedAt),
        toTimeValue(status.attributes.lastCompletedAt)
    );
};

const getProductsStatusLabel = (status: DataSyncAreaStatus) => {
    if (status.lastRunHadErrors) return "Needs attention";
    if (status.refreshRequested || status.inProgress) return "Refreshing";
    if (status.status === "Synced") return "Up to date";
    return "Needs refresh";
};

const getAttributeStatusLabel = (status: DataSyncAreaStatus, enabledAttributeCount: number) => {
    if (enabledAttributeCount === 0) return "No connected attributes";
    if (status.lastRunHadErrors) return "Needs attention";
    if (status.refreshRequested || status.inProgress) return "Refreshing";
    if (status.status === "Synced") return "Up to date";
    return "Needs refresh";
};

const isDataSyncRunning = (status?: DataSyncStatus) =>
    !!status && (
        status.catalog.refreshRequested
        || status.catalog.inProgress
        || status.attributes.refreshRequested
        || status.attributes.inProgress
    );

const dataSyncHadErrors = (status: DataSyncStatus) =>
    status.catalog.lastRunHadErrors || status.attributes.lastRunHadErrors;

const ProductsStatusRow = ({ status }: { status: DataSyncAreaStatus }) => {
    const lastUpdated = formatDateTime(status.lastCompletedAt);
    return (
        <StatusRow
            title="Products & collections"
            statusLabel={getProductsStatusLabel(status)}
            detail={lastUpdated ? `Last synced ${lastUpdated}` : "Not synced yet"}
        />
    );
};

const AttributeStatusRow = ({
    status,
    enabledAttributeCount,
    syncedAttributeValueCount,
    onOpenProductAttributes,
}: {
    status: DataSyncAreaStatus;
    enabledAttributeCount: number;
    syncedAttributeValueCount: number;
    onOpenProductAttributes?: () => void;
}) => {
    const lastUpdated = formatDateTime(status.lastCompletedAt);
    const detail = enabledAttributeCount === 0
        ? undefined
        : [
            lastUpdated ? `Last synced ${lastUpdated}` : "Not synced yet",
            `${enabledAttributeCount} ${enabledAttributeCount === 1 ? "attribute" : "attributes"}`,
            `${syncedAttributeValueCount} cached ${syncedAttributeValueCount === 1 ? "value" : "values"}`,
        ].join(" · ");

    return (
        <StatusRow
            title="Attribute values"
            statusLabel={getAttributeStatusLabel(status, enabledAttributeCount)}
            detail={detail}
            action={onOpenProductAttributes && <s-link onClick={onOpenProductAttributes}>{SETTINGS_SECTIONS.productAttributes}</s-link>}
        />
    );
};

const StatusRow = ({ title, statusLabel, detail, action }: {
    title: string;
    statusLabel: string;
    detail?: string;
    action?: React.ReactNode;
}) => (
    <s-stack gap="small-200">
        <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="base">
            <s-text fontWeight="semibold">{title}</s-text>
            <s-text tone={statusLabel === "Needs attention" ? "critical" : "auto"}>{statusLabel}</s-text>
        </s-grid>
        {detail && <s-text color="subdued">{detail}</s-text>}
        {action}
    </s-stack>
);

type DataSyncSettingsProps = {
    onOpenProductAttributes?: () => void;
};

const DataSyncSettings: React.FC<DataSyncSettingsProps> = ({ onOpenProductAttributes }) => {
    const [status, setStatus] = useState<DataSyncStatus | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState<string | undefined>(undefined);
    const [syncRequestedByUser, setSyncRequestedByUser] = useState(false);
    const [syncActivitySeen, setSyncActivitySeen] = useState(false);
    const [syncCompletionBaseline, setSyncCompletionBaseline] = useState(0);

    const loadStatus = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }

        try {
            const nextStatus = await agent.Settings.dataSyncStatus();
            setStatus(nextStatus);
            setError(undefined);
            return nextStatus;
        } catch {
            setError("Shopify sync status could not be loaded.");
            return undefined;
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const requestCatalogRefresh = async () => {
        setRefreshing(true);
        setError(undefined);
        setSuccess(undefined);
        setSyncRequestedByUser(true);
        setSyncActivitySeen(false);
        setSyncCompletionBaseline(getLatestCompletionTime(status));

        try {
            const nextStatus = await agent.Settings.refreshCatalogData();
            setStatus(nextStatus);

            if (isDataSyncRunning(nextStatus)) {
                setSyncActivitySeen(true);
            }
        } catch {
            setSyncRequestedByUser(false);
            setSyncActivitySeen(false);
            setSyncCompletionBaseline(0);
            setError("Sync failed. Please try again, or contact support if the issue continues.");
        } finally {
            setRefreshing(false);
        }
    };

    const syncRunning = isDataSyncRunning(status);
    const syncPending = syncRequestedByUser || syncRunning;

    useEffect(() => {
        if (!syncPending) return;

        const intervalId = window.setInterval(() => {
            loadStatus(false);
        }, 5000);

        return () => window.clearInterval(intervalId);
    }, [loadStatus, syncPending]);

    useEffect(() => {
        if (syncRunning) {
            setSuccess(undefined);
            if (syncRequestedByUser) {
                setSyncActivitySeen(true);
            }
            return;
        }

        if (!status || !syncRequestedByUser) return;

        const completedAfterRequest = getLatestCompletionTime(status) > syncCompletionBaseline;
        if (!syncActivitySeen && !completedAfterRequest) return;

        setSyncRequestedByUser(false);
        setSyncActivitySeen(false);
        setSyncCompletionBaseline(0);
        if (dataSyncHadErrors(status)) {
            setError("Sync failed. Please try again, or contact support if the issue continues.");
        } else {
            setSuccess("Shopify data synced successfully.");
        }
    }, [status, syncActivitySeen, syncCompletionBaseline, syncRequestedByUser, syncRunning]);

    const showProgress = syncPending && !error;
    const showSuccess = !!success && !syncPending;

    if (loading && !status) {
        return (
            <s-section>
                <s-stack alignItems="center" padding="base">
                    <s-spinner accessibilityLabel="Loading Shopify sync status" />
                </s-stack>
            </s-section>
        );
    }

    return (
        <>
            {error && <s-banner tone="critical">{error}</s-banner>}
            {showProgress && <s-banner tone="info">Shopify sync is running. This may take a few minutes.</s-banner>}
            {showSuccess && <s-banner tone="success">{success}</s-banner>}
            {status && <s-section heading="Shopify sync">
                <s-stack gap="base">
                    <ProductsStatusRow status={status.catalog} />
                    <s-divider />
                    <AttributeStatusRow
                        status={status.attributes}
                        enabledAttributeCount={status.enabledAttributeCount}
                        syncedAttributeValueCount={status.syncedAttributeValueCount}
                        onOpenProductAttributes={onOpenProductAttributes}
                    />
                    <s-divider />
                    <s-stack direction="inline">
                        <s-button variant="primary" onClick={requestCatalogRefresh} loading={refreshing} disabled={syncPending}>
                            {syncPending ? "Syncing…" : "Sync Shopify data"}
                        </s-button>
                    </s-stack>
                </s-stack>
            </s-section>}
        </>
    );
};

export default DataSyncSettings;
